import axios from 'axios';
import cookie from 'vue-cookies';
// eslint-disable-next-line import/no-cycle
import router from '@/router';
import { USER, USER_STATUS } from '@/utils/const';
import { isValidEmail, isApaLifeEmail } from '@/utils/emailValidation';

const data = {
  snackbar: {},
  loading: false,
  userInfo: cookie.get(USER) || null,
  userConfirmed: cookie.get(USER_STATUS) || false,
};

const getters = {
  isAuthenticated: (state) => state.userConfirmed && state.userInfo !== null,
  authenticatedUser: (state) => state.userInfo,
  userRoles: (state) => state.userInfo?.roles || [],
  isAdmin: (state) => {
    const roles = state.userInfo?.roles || [];
    return roles.includes('ADMIN') || roles.includes('ADMINISTRATOR') || roles.includes('STAFF');
  },
  isAgent: (state) => state.userInfo?.roles?.includes('AGENT') || false,
  agentInfo: (state) => ({
    agentCode: state.userInfo?.agentCode || 0,
    agentId: state.userInfo?.agentId || '',
    agentName: state.userInfo?.agentName || '',
  }),
};

const mutations = {
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SIGN_IN(state, userData) {
    const user = userData.user || userData;
    cookie.set(USER, user, '7d'); // Set cookie for 7 days
    cookie.set(USER_STATUS, true, '7d');
    state.userInfo = user;
    state.userConfirmed = true;
  },
  SNACKBAR(state, payload) {
    state.snackbar = payload.snackbar;
  },
  CLEAR_AUTHENTICATION(state) {
    cookie.remove(USER);
    cookie.remove(USER_STATUS);
    state.userInfo = null;
    state.userConfirmed = false;
  },
  UPDATE_USER_INFO(state, userInfo) {
    state.userInfo = { ...state.userInfo, ...userInfo };
    cookie.set(USER, state.userInfo, '7d');
  },
};

const actions = {
  async login({ commit }, userData) {
    commit('SET_LOADING', true);
    try {
      if (userData.user) {
        commit('SIGN_IN', userData.user);
        commit('SNACKBAR', {
          snackbar: {
            showing: true,
            text: 'Login successful!',
            color: 'success',
          },
        });
        return { success: true };
      }
      throw new Error('Invalid user data received');
    } catch (error) {
      commit('SNACKBAR', {
        snackbar: {
          showing: true,
          text: error.message || 'Login failed',
          color: 'error',
        },
      });
      return { success: false, error: error.message };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async authenticateUser({ commit }, credentials) {
    commit('SET_LOADING', true);
    try {
      const username = credentials.userName || credentials.email;

      // Validate username (agent code or email)
      if (isValidEmail(username) && !isApaLifeEmail(username)) {
        throw new Error('Only @apalife.co.ke email addresses are allowed');
      }

      const response = await axios.post('/lmsagent/login/AuthenticateSystemUser', {
        userName: username,
        password: credentials.password,
      });

      const userData = response.data;

      if (userData.user) {
        commit('SIGN_IN', userData.user);
        commit('SNACKBAR', {
          snackbar: {
            showing: true,
            text: 'Login successful!',
            color: 'success',
          },
        });

        // Determine redirect route based on user roles
        const roles = userData.user.roles || [];
        const isAdminUser = roles.includes('ADMINISTRATOR') || roles.includes('STAFF');
        const redirectRoute = isAdminUser ? '/admin' : '/dashboard';

        return { success: true, user: userData.user, redirectRoute };
      }
      throw new Error('Invalid credentials');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      commit('SNACKBAR', {
        snackbar: {
          showing: true,
          text: errorMessage,
          color: 'error',
        },
      });
      return { success: false, error: errorMessage };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  signOut({ commit }) {
    commit('CLEAR_AUTHENTICATION');
    commit('SNACKBAR', {
      snackbar: {
        showing: true,
        text: 'Logged out successfully',
        color: 'info',
      },
    });
    router.replace({ name: 'SignIn' });
  },

  checkAuthStatus({ commit, state }) {
    const user = cookie.get(USER);
    const status = cookie.get(USER_STATUS);

    if (user && status) {
      state.userInfo = user;
      state.userConfirmed = status;
    } else {
      commit('CLEAR_AUTHENTICATION');
    }
  },

  updateUserInfo({ commit }, userInfo) {
    commit('UPDATE_USER_INFO', userInfo);
  },
};

export default {
  namespaced: true,
  state: data,
  getters,
  mutations,
  actions,
};
